import moment from 'moment-timezone'

// tasks#202: the Onfleet estimated delivery window on the tracking pages.
// effectiveWindow() mirrors core-api NeverPastShift (spec §4.7) and the admin's services/EstimatedWindow.js — keep the
// three bodies identical.

const STEP_SECONDS = 300
const OFFSET_SUFFIX = /(Z|[+-]\d{2}:?\d{2})$/i

/**
 * Never in the past: a window whose start is not after now moves forward by 5 minutes per started 5 minutes behind,
 * so its start lands strictly after now; the width is kept.
 *
 * @param {moment.Moment} from
 * @param {moment.Moment} to
 * @param {moment.Moment} now
 * @returns {{from: moment.Moment, to: moment.Moment, shiftMinutes: number}}
 */
export function effectiveWindow(from, to, now) {
    if (from.isAfter(now)) return { from: from.clone(), to: to.clone(), shiftMinutes: 0 }

    const behind = Math.floor(now.valueOf() / 1000) - Math.floor(from.valueOf() / 1000)
    const shiftSeconds = STEP_SECONDS * (Math.floor(Math.max(0, behind) / STEP_SECONDS) + 1)

    return {
        from: from.clone().add(shiftSeconds, 'seconds'),
        to: to.clone().add(shiftSeconds, 'seconds'),
        shiftMinutes: shiftSeconds / 60,
    }
}

/**
 * HTTP / Mercure values are RFC3339 with an offset; binlog (Pusher) values are offset-less application time and are
 * read in the application timezone (core-api config `timezone`). Without a timezone an offset-less value cannot be
 * placed: null, the caller re-fetches the HTTP snapshot.
 *
 * @param {string|null|undefined} value
 * @param {string|null|undefined} timezone IANA name, e.g. Europe/London
 * @returns {moment.Moment|null}
 */
export function parseWindowValue(value, timezone) {
    if (!value || typeof value !== 'string') return null
    const trimmed = value.trim()
    let parsed
    if (OFFSET_SUFFIX.test(trimmed)) parsed = moment(trimmed)
    else if (timezone) parsed = moment.tz(trimmed, timezone)
    else return null
    return parsed.isValid() ? parsed : null
}

/**
 * True when the value is offset-less (a binlog value), i.e. it needs the application timezone.
 *
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function isOffsetLessWindowValue(value) {
    return typeof value === 'string' && value.trim() !== '' && !OFFSET_SUFFIX.test(value.trim())
}
